import type { GraphFrameRecord, GraphNode } from "@salesko/contracts";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { getGraphFrame, mergeCanonicalEntity, rekeyCanonicalEntity, splitCanonicalEntity } from "../../lib/api";
import { canonicalEntityIdForGraphNode, splitNodeRef } from "../lib/canonicalIdentity";
import type { CanonicalIdentityAction, CanonicalIdentityState } from "../types";

type UseCanonicalIdentityActionsOptions = {
  frameRecordId: string | null;
  onFrameReloaded: (record: GraphFrameRecord) => void;
  onInvalidateFrames: () => void;
};

type CanonicalIdentityActionVariables =
  | {
      action: "merge";
      sourceCanonicalEntityId: string;
      sourceLabel: string;
      targetCanonicalEntityId: string;
      targetLabel: string;
    }
  | {
      action: "rekey";
      label: string;
      sourceCanonicalEntityId: string;
      sourceLabel: string;
      targetLabel: string;
    }
  | {
      action: "split";
      label: string;
      node: GraphNode;
      sourceCanonicalEntityId: string;
      sourceLabel: string;
      targetLabel: string;
    };

type CanonicalIdentityActionResult = {
  action: CanonicalIdentityAction;
  frameRecordId: string;
  record: GraphFrameRecord;
  sourceLabel: string;
  targetLabel: string;
};

export function useCanonicalIdentityActions({
  frameRecordId,
  onFrameReloaded,
  onInvalidateFrames
}: UseCanonicalIdentityActionsOptions) {
  const [canonicalIdentityState, setCanonicalIdentityState] = useState<CanonicalIdentityState>({ status: "idle" });

  const mutation = useMutation<CanonicalIdentityActionResult, Error, CanonicalIdentityActionVariables>({
    mutationFn: async (variables) => {
      if (!frameRecordId) {
        throw new Error("Open a committed frame before editing canonical identity.");
      }

      if (variables.action === "merge") {
        await mergeCanonicalEntity(variables.sourceCanonicalEntityId, {
          targetCanonicalEntityId: variables.targetCanonicalEntityId,
          reason: `Merge ${variables.sourceLabel} into ${variables.targetLabel}`
        });
      } else if (variables.action === "rekey") {
        await rekeyCanonicalEntity(variables.sourceCanonicalEntityId, {
          label: variables.label,
          reason: `Re-key ${variables.sourceLabel} to ${variables.targetLabel}`
        });
      } else {
        await splitCanonicalEntity(variables.sourceCanonicalEntityId, {
          label: variables.label,
          nodes: [splitNodeRef(frameRecordId, variables.node)],
          reason: `Split ${variables.sourceLabel} to ${variables.targetLabel}`
        });
      }

      return {
        action: variables.action,
        frameRecordId,
        record: await getGraphFrame(frameRecordId),
        sourceLabel: variables.sourceLabel,
        targetLabel: variables.targetLabel
      };
    },
    onMutate: (variables) => {
      setCanonicalIdentityState({
        action: variables.action,
        sourceLabel: variables.sourceLabel,
        status: "running",
        targetLabel: variables.targetLabel
      });
    },
    onSuccess: (result) => {
      onFrameReloaded(result.record);
      onInvalidateFrames();
      setCanonicalIdentityState({
        action: result.action,
        frameRecordId: result.frameRecordId,
        sourceLabel: result.sourceLabel,
        status: "success",
        targetLabel: result.targetLabel
      });
    },
    onError: (error, variables) => {
      setCanonicalIdentityState({
        message: error.message || "Canonical identity action failed.",
        status: "error",
        ...(variables?.action ? { action: variables.action } : {}),
        ...(variables?.sourceLabel ? { sourceLabel: variables.sourceLabel } : {}),
        ...(variables?.targetLabel ? { targetLabel: variables.targetLabel } : {})
      });
    }
  });

  return {
    canonicalIdentityPending: mutation.isPending,
    canonicalIdentityState,
    mergeCanonicalIdentity: (sourceNode: GraphNode, targetNode: GraphNode) => {
      const sourceCanonicalEntityId = canonicalEntityIdForGraphNode(sourceNode);
      const targetCanonicalEntityId = canonicalEntityIdForGraphNode(targetNode);
      if (sourceCanonicalEntityId === targetCanonicalEntityId) {
        setCanonicalIdentityState({
          message: "Choose a different target identity before merging.",
          sourceLabel: sourceNode.label,
          status: "error",
          targetLabel: targetNode.label
        });
        return;
      }

      if (!confirmCanonicalIdentityAction(`Merge ${sourceNode.label} into ${targetNode.label}?`)) {
        return;
      }

      mutation.mutate({
        action: "merge",
        sourceCanonicalEntityId,
        sourceLabel: sourceNode.label,
        targetCanonicalEntityId,
        targetLabel: targetNode.label
      });
    },
    rekeyCanonicalIdentity: (sourceNode: GraphNode, label: string) => {
      const targetLabel = label.trim();
      if (targetLabel.length === 0) {
        setCanonicalIdentityState({
          message: "Enter a target label before re-keying.",
          sourceLabel: sourceNode.label,
          status: "error"
        });
        return;
      }

      if (!confirmCanonicalIdentityAction(`Re-key ${sourceNode.label} to ${targetLabel}?`)) {
        return;
      }

      mutation.mutate({
        action: "rekey",
        label: targetLabel,
        sourceCanonicalEntityId: canonicalEntityIdForGraphNode(sourceNode),
        sourceLabel: sourceNode.label,
        targetLabel
      });
    },
    resetCanonicalIdentityState: () => {
      setCanonicalIdentityState({ status: "idle" });
    },
    splitCanonicalIdentity: (sourceNode: GraphNode, label: string) => {
      const targetLabel = label.trim();
      if (targetLabel.length === 0) {
        setCanonicalIdentityState({
          message: "Enter a target label before splitting.",
          sourceLabel: sourceNode.label,
          status: "error"
        });
        return;
      }

      if (!confirmCanonicalIdentityAction(`Split selected ${sourceNode.label} node to ${targetLabel}?`)) {
        return;
      }

      mutation.mutate({
        action: "split",
        label: targetLabel,
        node: sourceNode,
        sourceCanonicalEntityId: canonicalEntityIdForGraphNode(sourceNode),
        sourceLabel: sourceNode.label,
        targetLabel
      });
    }
  };
}

function confirmCanonicalIdentityAction(message: string) {
  if (typeof window === "undefined") {
    return true;
  }

  return window.confirm(message);
}
